import { inject, Injectable } from '@angular/core';
import { map, Observable, of } from 'rxjs';

import { Option } from '@sapphire/result';
import { match } from 'ts-pattern';

import { BillingRuleRecord } from '@ps/user-settings-models';
import { User, getUserType } from '@ps/auth-data-access';
import { Nullable } from '@ps/utils';

import { SettingsApi } from '../api/settings.api';

import { AllBuyerSettings, CompanySettings } from '../models/settings.model';
import { CurrencySettings } from '../models/currency-settings.model';

@Injectable({ providedIn: 'root' })
export class SettingsService {
  private readonly _api = inject(SettingsApi);

  public getAllBuyerSettings(user: User): Observable<AllBuyerSettings> {
    // Suppliers don't have buyer settings
    if (getUserType(user) === 'supplier') return of({});
    return this._api.getAllBuyerSettings();
  }

  public getCompanySettings(
    companyId: number,
  ): Observable<Option<CompanySettings>> {
    return this._api
      .getCompanySettings(companyId)
      .pipe(map((companySettings) => Option.from(companySettings)));
  }

  public getCurrencySettings(fx: number): Observable<CurrencySettings> {
    return this._api.getCurrencySettings(fx);
  }

  public getBillingRules(
    user: User,
    allBuyerConfigs: AllBuyerSettings,
    buyerCompanyId: Nullable<number>,
  ): Observable<Nullable<BillingRuleRecord>> {
    const forOperator = match(
      buyerCompanyId &&
        allBuyerConfigs.enableBillingNumberRules?.includes(buyerCompanyId),
    )
      .with(true, () => this._api.getBillingRulesByCompany(buyerCompanyId!))
      .otherwise(() => of(null));

    const forBuyer = match(
      allBuyerConfigs.enableBillingNumberRules?.includes(user.companyId),
    )
      .with(true, () => this._api.getBillingRules())
      .otherwise(() => of(null));

    return match(getUserType(user))
      .with('operator', () => forOperator)
      .with('buyer', () => forBuyer)
      .otherwise(() => of(null));
  }
}
